/**
 * WhitelistUpgradeModal - Beautiful upgrade popup for non-whitelisted users
 * Shows when they try to access restricted features
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Stack
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import {
  Close as CloseIcon,
  LockOutlined as LockIcon,
  RocketLaunch as RocketIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    overflow: 'hidden',
    maxWidth: '480px',
    margin: '16px',
    background: 'transparent',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.7)',
    backdropFilter: 'blur(8px)',
  }
}));

const ModalContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  background: 'linear-gradient(135deg, #1e3a5f 0%, #0f172a 50%, #1e1b4b 100%)',
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '200px',
    background: 'radial-gradient(ellipse at top, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
    pointerEvents: 'none',
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  color: 'rgba(255, 255, 255, 0.7)',
  zIndex: 10,
  '&:hover': {
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  }
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: '100px',
  height: '100px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 24px',
  boxShadow: '0 20px 40px rgba(245, 158, 11, 0.4)',
  animation: `${float} 3s ease-in-out infinite`,
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(59, 130, 246, 0.2)',
  color: '#93c5fd',
  border: '1px solid rgba(59, 130, 246, 0.3)',
  fontWeight: 500,
  '& .MuiChip-icon': {
    color: '#60a5fa',
  }
}));

const UpgradeButton = styled(Button)(({ theme }) => ({
  padding: '16px 48px',
  borderRadius: '16px',
  fontSize: '18px',
  fontWeight: 700,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
  backgroundSize: '200% 100%',
  color: 'white',
  boxShadow: '0 10px 40px rgba(59, 130, 246, 0.5)',
  transition: 'all 0.3s ease',
  animation: `${shimmer} 3s linear infinite`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 15px 50px rgba(59, 130, 246, 0.6)',
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '12px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    transform: 'translateX(4px)',
  }
}));

const features = [
  { icon: <AnalyticsIcon />, text: 'Advanced Analytics & Insights' },
  { icon: <StarIcon />, text: 'AI-Powered Review Replies' },
  { icon: <SpeedIcon />, text: 'Performance Tracking' },
  { icon: <SupportIcon />, text: 'Priority Support' },
];

const WhitelistUpgradeModal = ({ open, onClose, featureName, modalType = 'whitelist' }) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  // Different content based on modal type
  const isSubscriptionModal = modalType === 'subscription';
  const title = isSubscriptionModal ? 'Upgrade to Access Monthly Plan' : 'Unlock Premium Features';
  const subtitle = isSubscriptionModal
    ? 'Purchase a subscription plan (Basic, Advanced, or Pro) to unlock the Monthly Plan feature and automate your Google Business Profile content strategy.'
    : `Upgrade your plan to access ${featureName || 'this feature'} and supercharge your Google Business Profile management.`;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <ModalContent>
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>

        <Box sx={{ p: 4, pt: 5, textAlign: 'center', position: 'relative', zIndex: 1 }}>
          {/* Icon */}
          <IconContainer>
            <LockIcon sx={{ fontSize: 48, color: 'white' }} />
          </IconContainer>

          {/* Feature Badge */}
          {featureName && (
            <FeatureChip
              icon={<RocketIcon />}
              label={featureName}
              sx={{ mb: 3 }}
            />
          )}

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #93c5fd 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              mb: 4,
              maxWidth: '360px',
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            {subtitle}
          </Typography>

          {/* Features List */}
          <Stack spacing={1.5} sx={{ mb: 4 }}>
            {features.map((feature, index) => (
              <FeatureItem key={index}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'rgba(59, 130, 246, 0.2)',
                    color: '#60a5fa',
                  }}
                >
                  {feature.icon}
                </Avatar>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  {feature.text}
                </Typography>
                <CheckIcon
                  sx={{
                    ml: 'auto',
                    color: '#22c55e',
                    fontSize: 20,
                  }}
                />
              </FeatureItem>
            ))}
          </Stack>

          {/* CTA Button */}
          <UpgradeButton
            variant="contained"
            onClick={handleUpgrade}
            startIcon={<RocketIcon />}
          >
            View Pricing Plans
          </UpgradeButton>
        </Box>
      </ModalContent>
    </StyledDialog>
  );
};

export default WhitelistUpgradeModal;
